import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  ArrowLeftRight, Landmark, Users, RefreshCw, Download, AlertTriangle, Radio, Clock, CheckCircle2, Link2, Link2Off,
} from "lucide-react";
import { euro, dateTime } from "@/lib/format";
import { toast } from "sonner";

const currentMonth = () => new Date().toISOString().slice(0, 7);

const STATUS_STYLES = {
  paid: "bg-[hsl(160_84%_32%)] text-white border-0",
  pending: "bg-[hsla(38,92%,50%,0.15)] text-[hsl(38_92%_38%)] border-0",
  in_transit: "bg-[hsla(38,92%,50%,0.15)] text-[hsl(38_92%_38%)] border-0",
  failed: "bg-destructive/15 text-destructive border-0",
  canceled: "bg-secondary text-muted-foreground border-0",
  reversed: "bg-destructive/15 text-destructive border-0",
  partially_reversed: "bg-[hsla(38,92%,50%,0.15)] text-[hsl(38_92%_38%)] border-0",
};

const StatCard = ({ icon: Icon, label, value, hint, testId }) => (
  <Card className="p-4 rounded-2xl" data-testid={testId}>
    <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Icon className="h-4 w-4 text-primary" /> {label}</div>
    <p className="font-display text-2xl">{value}</p>
    {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
  </Card>
);

const Destination = ({ m }) => {
  const d = m.destination || {};
  if (m.kind === "creator_transfer") {
    return (
      <div className="min-w-0">
        <p className="font-medium text-sm truncate">{d.username ? `@${d.username}` : (d.email || "Créateur")}</p>
        <p className="text-[11px] text-muted-foreground truncate font-mono">{d.account_id || "—"}</p>
      </div>
    );
  }
  return (
    <div className="min-w-0">
      <p className="font-medium text-sm truncate">{d.bank_name || "Compte bancaire plateforme"}</p>
      <p className="text-[11px] text-muted-foreground">••••{d.last4 || "????"}{m.automatic ? " · automatique" : " · manuel"}</p>
    </div>
  );
};

export const TransfersPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kind, setKind] = useState("all");
  const [month, setMonth] = useState(currentMonth());
  const [allTime, setAllTime] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [liveTick, setLiveTick] = useState(null);
  const loadingRef = useRef(false);

  const query = useCallback(() => {
    const p = new URLSearchParams({ kind });
    if (!allTime && month) p.set("month", month);
    return p.toString();
  }, [kind, month, allTime]);

  const load = useCallback(async (silent = false) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/admin/transfers?${query()}`);
      setData(res.data);
    } catch (e) {
      setError(apiError(e));
      if (!silent) toast.error(apiError(e));
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [query]);

  useEffect(() => { load(); }, [load]);

  // Temps réel : dès qu'une notification "virement" arrive par SSE, on recharge la liste.
  useEffect(() => {
    const onNotif = (ev) => {
      const n = ev.detail;
      if (n && n.type === "admin_transfer") {
        setLiveTick(new Date());
        load(true);
      }
    };
    window.addEventListener("mihod:notification", onNotif);
    return () => window.removeEventListener("mihod:notification", onNotif);
  }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/admin/transfers.csv?${query()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mihodpay-virements-${allTime ? "tout" : month}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export CSV des virements téléchargé");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setExporting(false);
    }
  };

  const s = data?.summary || {};
  const rows = data?.movements || [];

  return (
    <div className="space-y-4" data-testid="admin-transfers-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">Historique des virements Stripe : vers les créateurs (Connect) et vers votre banque (Revolut).</p>
          <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5" data-testid="transfers-live-indicator">
            <Radio className="h-3 w-3 text-[hsl(160_84%_38%)]" /> Mise à jour en temps réel
            {liveTick && <span>· dernier événement {dateTime(liveTick.toISOString())}</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="w-44 rounded-xl" data-testid="transfers-kind-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les virements</SelectItem>
              <SelectItem value="transfer">Créateurs (Connect)</SelectItem>
              <SelectItem value="payout">Plateforme (banque)</SelectItem>
            </SelectContent>
          </Select>
          <Input type="month" value={month} onChange={(e) => { setMonth(e.target.value); setAllTime(false); }}
            disabled={allTime} className="w-40 rounded-xl" data-testid="transfers-month-input" />
          <Button variant={allTime ? "default" : "outline"} className="rounded-xl" onClick={() => setAllTime((v) => !v)} data-testid="transfers-alltime-toggle">
            Tout
          </Button>
          <Button variant="outline" className="rounded-xl" onClick={() => load()} disabled={loading} data-testid="transfers-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button className="rounded-xl" onClick={exportCsv} disabled={exporting || loading} data-testid="transfers-export-csv">
            <Download className="h-4 w-4 mr-1" /> {exporting ? "Export…" : "CSV"}
          </Button>
        </div>
      </div>

      {loading && !data ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
          <Skeleton className="h-48 rounded-2xl" />
        </div>
      ) : error && !data ? (
        <div className="text-center py-12 text-muted-foreground" data-testid="transfers-error">
          <AlertTriangle className="h-6 w-6 mx-auto mb-2 text-destructive" />
          <p className="text-sm">{error}</p>
          <Button variant="outline" className="mt-3 rounded-xl" onClick={() => load()}><RefreshCw className="h-4 w-4 mr-1" /> Réessayer</Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard icon={Users} label="Versé aux créateurs" value={euro(s.creator_transfers_paid || 0)} hint={`${s.creator_transfers_count || 0} virement(s) Connect`} testId="transfers-stat-creators" />
            <StatCard icon={Landmark} label="Versé à la plateforme" value={euro(s.platform_payouts_paid || 0)} hint={`${s.platform_payouts_count || 0} payout(s) banque`} testId="transfers-stat-platform" />
            <StatCard icon={Clock} label="Payouts en cours" value={euro(s.platform_payouts_pending || 0)} hint="En attente / en transit" testId="transfers-stat-pending" />
            <StatCard icon={AlertTriangle} label="Échecs / inversions" value={s.failed_count || 0} hint={s.unreconciled_count ? `${s.unreconciled_count} non rapproché(s) en base` : "Tout est rapproché"} testId="transfers-stat-failed" />
          </div>

          {(data?.errors || []).length > 0 && (
            <Card className="p-3 rounded-2xl border-[hsl(38_92%_50%)]/40 text-xs text-[hsl(38_92%_38%)] flex items-start gap-2" data-testid="transfers-warnings">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <div>{data.errors.map((e, i) => <p key={i}>{e}</p>)}</div>
            </Card>
          )}

          {rows.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground" data-testid="transfers-empty">
              <div className="mx-auto h-14 w-14 rounded-2xl bg-secondary grid place-items-center mb-3"><ArrowLeftRight className="h-6 w-6" /></div>
              <p className="font-medium text-foreground">Aucun virement {allTime ? "" : "ce mois-ci"}</p>
              <p className="text-xs mt-1">Les retraits des créateurs et vos payouts vers Revolut apparaîtront ici dès qu'ils seront émis par Stripe.</p>
            </div>
          ) : (
            <Card className="rounded-2xl overflow-hidden" data-testid="transfers-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Bénéficiaire</TableHead>
                    <TableHead className="text-right">Montant</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Référence</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((m) => (
                    <TableRow key={m.id} data-testid={`transfer-row-${m.id}`}>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{dateTime(m.created_at)}</TableCell>
                      <TableCell className="text-xs">
                        {m.kind === "creator_transfer" ? (
                          <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5 text-primary" /> Créateur</span>
                        ) : (
                          <span className="inline-flex items-center gap-1"><Landmark className="h-3.5 w-3.5 text-primary" /> Plateforme</span>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[200px]"><Destination m={m} /></TableCell>
                      <TableCell className="text-right font-semibold text-sm whitespace-nowrap">
                        {euro(m.amount)}
                        {m.amount_reversed > 0 && <p className="text-[10px] text-destructive font-normal">-{euro(m.amount_reversed)} inversé</p>}
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUS_STYLES[m.status] || "bg-secondary text-muted-foreground border-0"} data-testid={`transfer-status-${m.id}`}>{m.status_label}</Badge>
                        {m.arrival_date && <p className="text-[10px] text-muted-foreground mt-0.5">Arrivée {m.arrival_date.slice(0, 10)}</p>}
                        {m.failure_message && <p className="text-[10px] text-destructive mt-0.5">{m.failure_message}</p>}
                      </TableCell>
                      <TableCell className="text-[11px] font-mono text-muted-foreground">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center gap-1 cursor-default">
                                {m.reconciled ? <Link2 className="h-3.5 w-3.5 text-[hsl(160_84%_38%)]" /> : <Link2Off className="h-3.5 w-3.5 text-[hsl(38_92%_38%)]" />}
                                {m.id.slice(0, 14)}…
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="text-xs">
                              {m.id}<br />{m.reconciled ? "Rapproché avec la base MIHOD PAY" : "Présent dans Stripe uniquement (non rapproché)"}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
          <p className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid="transfers-source-note">
            <CheckCircle2 className="h-3 w-3" /> Source de vérité : Stripe ({data?.stripe_mode === "live" ? "LIVE — argent réel" : "TEST"}). Généré {data?.generated_at ? dateTime(data.generated_at) : "—"}.
          </p>
        </>
      )}
    </div>
  );
};
